import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { SkeletonModule } from 'primeng/skeleton';
import { UserTrackingService } from '@myarvato/core/services';
import { CarrierNewsService } from '../../services/carrier-news.service';
import { CarrierNewsItemDto } from '../../models/carrier-news-item-dto';
import { CustomizationTranslationService } from '@myarvato/shared/services';
import { DataView } from 'primeng/dataview';
import { TranslatePipe } from '@ngx-translate/core';
import { NewsItemComponent } from './components/news-item/news-item.component';
import { HttpError } from '@myarvato/shared/models';
import { ErrorMessageComponent } from '@myarvato/components/message';

@Component({
  selector: 'transport-news-update',
  imports: [SkeletonModule, DataView, TranslatePipe, NewsItemComponent, ErrorMessageComponent],
  templateUrl: './news-update.component.html',
  styleUrl: './news-update.component.scss',
})
export class NewsUpdateComponent implements OnInit {
  // Injections
  private readonly userTrackingService = inject(UserTrackingService);
  private readonly carrierNewsService = inject(CarrierNewsService);
  private readonly customizationTranslationService = inject(CustomizationTranslationService);

  // Fields
  protected readonly trackingCategory = 'Transport - Overview - News & Updates';

  // Change detection
  protected readonly state = signal<{
    loading: boolean;
    error: unknown | undefined;
    data: CarrierNewsItemDto[] | undefined;
  }>({
    loading: true,
    error: undefined,
    data: undefined,
  });

  protected readonly isError = computed(() => this.state().error !== undefined);
  protected readonly isSuccess = computed(() => this.state().data !== undefined && !this.isError() && !this.isEmpty());
  protected readonly isLoading = computed(() => this.state().loading);
  protected readonly isEmpty = computed(() => this.state().data !== undefined && (this.state()?.data?.length ?? 0) === 0);
  protected readonly data = computed(() => this.state().data ?? []);

  protected readonly errorMessage = computed(() => {
    const exception = this.state().error as HttpError;
    const errorCode = exception?.error?.errorCode || 'G0400';
    const errorTranslationTitle = this.customizationTranslationService.getTranslation(`errors.${errorCode}.title`, 'errors.G0400.title');
    const errorTranslationDescription = this.customizationTranslationService.getTranslation(
      `errors.${errorCode}.description`,
      'errors.G0400.description',
    );

    return {
      title: errorTranslationTitle,
      description: errorTranslationDescription,
    };
  });

  public ngOnInit(): void {
    this.carrierNewsService.getCarrierNews().subscribe({
      next: (data) => {
        this.state.set({ loading: false, error: undefined, data: data });
      },
      error: (error) => {
        console.error(error);
        this.state.set({ loading: false, error: error, data: undefined });
      },
    });
  }

  protected onPaginatorChanged() {
    this.userTrackingService.trackEvent(this.trackingCategory, 'Paginate');
  }
}
